import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ffeAPI, vendorsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Search, Loader2, Pencil, Trash2, Package, Eye,
  Sofa, FolderOpen, DoorOpen, Store, Download,
} from 'lucide-react';
import { generateFFESchedulePDF } from '../lib/pdfExport';

const CATEGORIES = [
  'furniture', 'fixtures', 'equipment', 'materials', 'lighting',
  'fabrics', 'accessories', 'artwork', 'plumbing', 'hardware', 'other',
];

const STATUSES = ['sourcing', 'quoted', 'approved', 'ordered', 'shipped', 'received', 'installed'];

const STATUS_COLORS = {
  sourcing: 'bg-gray-100 text-gray-700',
  quoted: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  ordered: 'bg-purple-100 text-purple-700',
  shipped: 'bg-amber-100 text-amber-700',
  received: 'bg-teal-100 text-teal-700',
  installed: 'bg-emerald-100 text-emerald-800',
};

const fmt = (n) => typeof n === 'number' && n > 0 ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—';

export default function FFELibrary() {
  const { isAdmin, isStaff } = useAuth();
  const canEdit = isAdmin || isStaff;
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const emptyForm = {
    name: '', category: 'furniture', description: '', sku: '', dimensions: '',
    material: '', finish: '', color: '', image_url: '', vendor_id: '',
    vendor_name: '', trade_price: '', markup_percentage: 30, lead_time: '',
    quantity: 1, status: 'sourcing', project_id: '', room_id: '', notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (projectFilter !== 'all') params.project_id = projectFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const [itemsRes, summaryRes, vendorRes] = await Promise.all([
        ffeAPI.list(params),
        ffeAPI.summary(),
        vendorsAPI.list(),
      ]);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
      setVendors(vendorRes.data);
    } catch (err) {
      console.error('Failed to load FF&E:', err);
    } finally {
      setLoading(false);
    }
  }, [projectFilter, categoryFilter, statusFilter, searchTerm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      if (editing) {
        await ffeAPI.update(editing.ffe_id, form);
      } else {
        await ffeAPI.create(form);
      }
      fetchData();
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await ffeAPI.delete(id); fetchData(); } catch (e) { console.error(e); }
  };

  const handleStatusChange = async (ffeId, newStatus) => {
    try { await ffeAPI.update(ffeId, { status: newStatus }); fetchData(); } catch (e) { console.error(e); }
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name, category: item.category || 'other', description: item.description || '',
      sku: item.sku || '', dimensions: item.dimensions || '', material: item.material || '',
      finish: item.finish || '', color: item.color || '', image_url: item.image_url || '',
      vendor_id: item.vendor_id || '', vendor_name: item.vendor_name || '',
      trade_price: item.trade_price || '', markup_percentage: item.markup_percentage || 0,
      lead_time: item.lead_time || '', quantity: item.quantity || 1,
      status: item.status || 'sourcing', project_id: item.project_id || '',
      room_id: item.room_id || '', notes: item.notes || '',
    });
    setDialogOpen(true);
  };

  // Compute client price live
  const liveClientPrice = (parseFloat(form.trade_price) || 0) * (1 + (parseFloat(form.markup_percentage) || 0) / 100);

  const filterProjects = summary?.filters?.projects || [];
  const filterRooms = (summary?.filters?.rooms || []).filter(r => !form.project_id || r.project_id === form.project_id);

  return (
    <Layout>
      <div className="space-y-6" data-testid="ffe-library-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight flex items-center gap-3">
              <Sofa className="h-7 w-7 text-muted-foreground" />FF&E Library
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Centralized product specifications catalog</p>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              {items.length > 0 && (
                <Button variant="outline" onClick={() => generateFFESchedulePDF(items, 'All Projects')} data-testid="export-ffe-pdf">
                  <Download className="h-4 w-4 mr-2" />Export PDF
                </Button>
              )}
              <Button onClick={() => { setForm(emptyForm); setEditing(null); setDialogOpen(true); }} data-testid="add-ffe-btn">
                <Plus className="h-4 w-4 mr-2" />Add Item
              </Button>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wider">Total Items</p><p className="text-2xl font-light mt-1" data-testid="ffe-total">{summary?.total_items || 0}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wider">Trade Value</p><p className="text-2xl font-light mt-1">{fmt(summary?.total_trade_value)}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wider">Client Value</p><p className="text-2xl font-light mt-1">{fmt(summary?.total_client_value)}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wider">Total Markup</p><p className="text-2xl font-light mt-1 text-green-600">{fmt(summary?.total_markup)}</p></CardContent></Card>
          <Card><CardContent className="pt-5 pb-4"><p className="text-xs text-muted-foreground uppercase tracking-wider">Installed</p><p className="text-2xl font-light mt-1">{summary?.by_status?.installed || 0}</p></CardContent></Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search items, SKU, vendor..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="ffe-search" />
              </div>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="ffe-project-filter"><SelectValue placeholder="All Projects" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Projects</SelectItem>{filterProjects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-36" data-testid="ffe-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORIES.map(c => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}</SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-36" data-testid="ffe-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Status</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <Card><CardContent className="py-16 text-center"><Sofa className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" /><h3 className="text-lg font-medium mb-2">No FF&E items found</h3><p className="text-sm text-muted-foreground">{searchTerm || projectFilter !== 'all' || categoryFilter !== 'all' || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Add your first furniture, fixture, or equipment item'}</p></CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Item</TableHead>
                    <TableHead className="text-xs">Category</TableHead>
                    <TableHead className="text-xs">Project / Room</TableHead>
                    <TableHead className="text-xs">Vendor</TableHead>
                    <TableHead className="text-xs text-center">Qty</TableHead>
                    <TableHead className="text-xs text-right">Trade</TableHead>
                    <TableHead className="text-xs text-right">Client</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map(item => (
                    <TableRow key={item.ffe_id} data-testid={`ffe-row-${item.ffe_id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{item.name}</p>
                          <div className="flex gap-2 text-xs text-muted-foreground">
                            {item.sku && <span className="font-mono">{item.sku}</span>}
                            {item.material && <span>{item.material}</span>}
                            {item.finish && <span>{item.finish}</span>}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><span className="capitalize text-xs px-2 py-0.5 rounded-full bg-muted">{item.category}</span></TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {item.project_name && <Link to={`/projects/${item.project_id}`} className="hover:text-primary transition-colors flex items-center gap-1"><FolderOpen className="h-3 w-3" />{item.project_name}</Link>}
                          {item.room_name && <span className="text-xs text-muted-foreground flex items-center gap-1"><DoorOpen className="h-3 w-3" />{item.room_name}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.vendor_name ? (
                          <Link to={`/vendors/${item.vendor_id}`} className="hover:text-primary transition-colors flex items-center gap-1"><Store className="h-3 w-3" />{item.vendor_name}</Link>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-center text-sm">{item.quantity}</TableCell>
                      <TableCell className="text-right text-sm">{fmt(item.trade_price)}</TableCell>
                      <TableCell className="text-right text-sm font-medium">{fmt(item.client_price)}</TableCell>
                      <TableCell>
                        {canEdit ? (
                          <Select value={item.status} onValueChange={v => handleStatusChange(item.ffe_id, v)}>
                            <SelectTrigger className="h-7 w-24 text-[10px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[item.status] || ''}`}>{item.status}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelected(item); setViewOpen(true); }} data-testid={`view-ffe-${item.ffe_id}`}><Eye className="h-3.5 w-3.5" /></Button>
                          {canEdit && (
                            <>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(item.ffe_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* View Dialog (Tear Sheet) */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Product Specification</DialogTitle></DialogHeader>
            {selected && (
              <div className="space-y-4 py-2" data-testid="ffe-tearsheet">
                <div className="flex items-start gap-4">
                  {selected.image_url ? (
                    <img src={selected.image_url} alt={selected.name} className="w-24 h-24 object-cover rounded bg-muted" />
                  ) : (
                    <div className="w-24 h-24 bg-muted rounded flex items-center justify-center"><Sofa className="h-8 w-8 text-muted-foreground/30" /></div>
                  )}
                  <div>
                    <h3 className="text-lg font-medium">{selected.name}</h3>
                    <p className="text-sm text-muted-foreground capitalize">{selected.category}</p>
                    {selected.sku && <p className="text-xs font-mono text-muted-foreground">SKU: {selected.sku}</p>}
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize mt-1 inline-block ${STATUS_COLORS[selected.status] || ''}`}>{selected.status}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selected.dimensions && <div><p className="text-muted-foreground text-xs">Dimensions</p><p>{selected.dimensions}</p></div>}
                  {selected.material && <div><p className="text-muted-foreground text-xs">Material</p><p>{selected.material}</p></div>}
                  {selected.finish && <div><p className="text-muted-foreground text-xs">Finish</p><p>{selected.finish}</p></div>}
                  {selected.color && <div><p className="text-muted-foreground text-xs">Color</p><p>{selected.color}</p></div>}
                  {selected.lead_time && <div><p className="text-muted-foreground text-xs">Lead Time</p><p>{selected.lead_time}</p></div>}
                  {selected.vendor_name && <div><p className="text-muted-foreground text-xs">Vendor</p><p>{selected.vendor_name}</p></div>}
                  {selected.project_name && <div><p className="text-muted-foreground text-xs">Project</p><p>{selected.project_name}</p></div>}
                  {selected.room_name && <div><p className="text-muted-foreground text-xs">Room</p><p>{selected.room_name}</p></div>}
                </div>
                <div className="border-t pt-3 grid grid-cols-4 gap-3 text-center">
                  <div><p className="text-xs text-muted-foreground">Qty</p><p className="font-medium">{selected.quantity}</p></div>
                  <div><p className="text-xs text-muted-foreground">Trade</p><p className="font-medium">{fmt(selected.trade_price)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Markup</p><p className="font-medium">{selected.markup_percentage}%</p></div>
                  <div><p className="text-xs text-muted-foreground">Client</p><p className="font-medium text-green-600">{fmt(selected.client_price)}</p></div>
                </div>
                {selected.description && <p className="text-sm text-muted-foreground border-t pt-3">{selected.description}</p>}
                {selected.notes && <p className="text-sm text-muted-foreground">{selected.notes}</p>}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit FF&E Item' : 'Add FF&E Item'}</DialogTitle>
              <DialogDescription>Specify product details, pricing, and assignment</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2 space-y-2"><Label>Item Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="e.g., Eames Lounge Chair" data-testid="ffe-name-input" /></div>
                  <div className="space-y-2"><Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="ffe-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}><span className="capitalize">{c}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Product details, specifications..." /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>SKU</Label><Input value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="Product code" /></div>
                  <div className="space-y-2"><Label>Dimensions</Label><Input value={form.dimensions} onChange={e => setForm({ ...form, dimensions: e.target.value })} placeholder="L x W x H" /></div>
                  <div className="space-y-2"><Label>Material</Label><Input value={form.material} onChange={e => setForm({ ...form, material: e.target.value })} placeholder="e.g., Teak Wood" /></div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Finish</Label><Input value={form.finish} onChange={e => setForm({ ...form, finish: e.target.value })} placeholder="e.g., Matt Lacquer" /></div>
                  <div className="space-y-2"><Label>Color</Label><Input value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder="e.g., Walnut" /></div>
                  <div className="space-y-2"><Label>Image URL</Label><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
                </div>

                {/* Vendor & Pricing */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Vendor</Label>
                    <Select value={form.vendor_id || 'none'} onValueChange={v => {
                      const vendor = vendors.find(vn => vn.vendor_id === v);
                      setForm({ ...form, vendor_id: v === 'none' ? '' : v, vendor_name: vendor?.name || '' });
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No Vendor</SelectItem>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Lead Time</Label><Input value={form.lead_time} onChange={e => setForm({ ...form, lead_time: e.target.value })} placeholder="e.g., 4-6 weeks" /></div>
                </div>
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2"><Label>Qty</Label><Input type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Trade Price (₹)</Label><Input type="number" step="0.01" value={form.trade_price} onChange={e => setForm({ ...form, trade_price: e.target.value })} data-testid="ffe-trade-price" /></div>
                  <div className="space-y-2"><Label>Markup %</Label><Input type="number" step="0.1" value={form.markup_percentage} onChange={e => setForm({ ...form, markup_percentage: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Client Price</Label><div className="h-9 flex items-center px-3 bg-muted rounded text-sm font-medium text-green-600">{fmt(liveClientPrice)}</div></div>
                </div>

                {/* Assignment */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label>Project</Label>
                    <Select value={form.project_id || 'none'} onValueChange={v => setForm({ ...form, project_id: v === 'none' ? '' : v, room_id: '' })}>
                      <SelectTrigger><SelectValue placeholder="Assign to project" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No Project</SelectItem>{filterProjects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Room</Label>
                    <Select value={form.room_id || 'none'} onValueChange={v => setForm({ ...form, room_id: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue placeholder="Assign to room" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">No Room</SelectItem>{filterRooms.map(r => <SelectItem key={r.room_id} value={r.room_id}>{r.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes..." /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="save-ffe-btn">
                  {formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editing ? 'Update' : 'Add Item'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
